import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { DirectValuesProposal } from '@Pages/passwordless/welcome/components/valueProposal/DirectValuesProposal.tsx';
import { ValueProposalWyoming } from '@Pages/passwordless/welcome/components/valueProposal/ValueProposalWyoming.tsx';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { DirectWelcomeHeader } from '@Pages/passwordless/welcome/components/DirectWelcomeHeader.tsx';
import { PASSWORD_LESS_PARTNER_IDS_MAP, SAGE_PARTNER_IDS } from '@Types/consts';
import { WelcomeForm } from '@Pages/passwordless/welcome/welcomeForm/WelcomeForm.tsx';
import { ValuesProposalPasswordless } from './components/valueProposal/ValuesProposalPasswordless.tsx';
import { WelcomeBonusChip } from './components/WelcomeBonusChip.tsx';
import type { SagePartnerIds } from '@Types/consts';

export const WelcomePage = () => {
  const { t } = useTranslation();
  const isEmbedded = useBoundStore((state) => state.onboardingSlice.getIsEmbedded());
  const partnerId = useBoundStore((state) => state.customer.partnerId);
  const setCustomerAccessToken = useBoundStore((state) => state.setCustomerAccessToken);
  const setAuthUserAccessToken = useBoundStore((state) => state.setAuthUserAccessToken);
  const isPartnerFlow = useBoundStore((state) => state.onboardingSlice.isPartnerFlow);
  const setIsAppSubmitted = useBoundStore((state) => state.onboardingSlice.setIsAppSubmitted);

  const getValueProposalComponent = () => {
    if (!isEmbedded) {
      return <DirectValuesProposal />;
    }

    switch (partnerId) {
      case PASSWORD_LESS_PARTNER_IDS_MAP.WYOMING_INTEG:
      case PASSWORD_LESS_PARTNER_IDS_MAP.WYOMING_SAGE:
        return <ValueProposalWyoming />;
      case PASSWORD_LESS_PARTNER_IDS_MAP.DNB_INTEG_1:
      case PASSWORD_LESS_PARTNER_IDS_MAP.DNB_INTEG_2:
      case PASSWORD_LESS_PARTNER_IDS_MAP.DNB_PROD:
      case PASSWORD_LESS_PARTNER_IDS_MAP.LILI_PARTNER:
      default:
        return <ValuesProposalPasswordless />;
    }
  };

  // Clear access tokens when Welcome Page mounts, but NOT in partner flow
  useEffect(() => {
    // First check partnerId, then fallback to isPartnerFlow
    const shouldPreserveTokens = partnerId || isPartnerFlow;

    if (!shouldPreserveTokens) {
      setCustomerAccessToken('');
      setAuthUserAccessToken('');
    }
  }, [setCustomerAccessToken, setAuthUserAccessToken, partnerId, isPartnerFlow]);

  useEffect(() => {
    setIsAppSubmitted(false);
  }, []);

  return (
    <div
      id='welcome-page-container'
      className='flex flex-col tab-landscape:flex-row items-center tab-landscape:items-stretch w-full h-dvh overflow-y-scroll overflow-x-hidden fade-in'>
      <div
        id='welcome-form-section'
        className='flex justify-center w-full tab-landscape:basis-1/2 shrink-0 h-fit tab-landscape:h-full bg-surface-primary-bw-00 md:px-[24px] px-[16px] pt-[16px] pb-[32px] overflow-x-hidden'>
        <div className='flex flex-col h-full max-w-[420px] w-full justify-center'>
          {isEmbedded && SAGE_PARTNER_IDS.includes(partnerId as SagePartnerIds) && (
            <div className='sm:hidden self-center mb-[12px]'>
              <WelcomeBonusChip />
            </div>
          )}
          <div className='mb-[28px]'>
            {isEmbedded ? (
              <>
                <div className='hidden sm:block'>
                  <Heading.H3 fontWeight={FontWeight.SEMIBOLD}>
                    {t('pages.homePasswordless.title')}
                  </Heading.H3>
                </div>
                <div className='sm:hidden text-center'>
                  <Heading.H7 fontWeight={FontWeight.SEMIBOLD}>
                    {t('pages.homePasswordless.title')}
                  </Heading.H7>
                </div>
              </>
            ) : (
              <DirectWelcomeHeader />
            )}
          </div>
          <WelcomeForm />
        </div>
      </div>
      <div
        id='welcome-value-proposal-section'
        className='flex justify-center w-full tab-landscape:max-h-[100dvh] tab-landscape:overflow-auto overflow-x-hidden tab-landscape:basis-1/2 shrink-0 tab-landscape:min-h-full grow bg-surface-brand-static-lili'>
        {getValueProposalComponent()}
      </div>
    </div>
  );
};
